import { StyleSheet, css } from 'aphrodite'
import React from 'react'

interface RoadmapItemProps {
  number: number
  title: string
  description: string
}

interface RoadmapSectionProps {
  title: string
  description: string
  items: RoadmapItemProps[]
  imageSrc: string
  imageAlt: string
}

export default function RoadmapSection({
  title,
  description,
  items,
  imageSrc,
  imageAlt,
}: RoadmapSectionProps) {
  return (
    <section className={css(styles.section)}>
      <div className={css(styles.content)}>
        <h2 className={css(styles.title)}>{title}</h2>
        <p className={css(styles.description)}>{description}</p>
        <div className={css(styles.list)}>
          {items.map((item, index) => (
            <div
              key={item.number}
              className={css(
                styles.listItem,
                index === items.length - 1 && styles.lastItem,
              )}
            >
              <div className={css(styles.itemNumberContainer)}>
                <span className={css(styles.itemNumber)}>{item.number}</span>
              </div>
              <div className={css(styles.itemContent)}>
                <h3 className={css(styles.itemTitle)}>{item.title}</h3>
                <p className={css(styles.itemDescription)}>
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className={css(styles.imageContainer)}>
        <img
          src={imageSrc}
          alt={imageAlt}
          className={css(styles.image)}
          loading="lazy"
        />
      </div>
    </section>
  )
}

const styles = StyleSheet.create({
  section: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: '32px',
    padding: '40px 1rem',
    alignItems: 'center',
    backgroundColor: 'transparent',
    '@media (min-width: 768px)': {
      gap: '48px',
      padding: '60px 2rem',
    },
    '@media (min-width: 1024px)': {
      gridTemplateColumns: '1fr 1fr',
      gap: '64px',
      padding: '80px 3rem',
    },
    '@media (min-width: 1280px)': {
      padding: '100px 5rem',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    textAlign: 'center',
    '@media (min-width: 768px)': {
      gap: '20px',
    },
    '@media (min-width: 1024px)': {
      gap: '24px',
      textAlign: 'left',
    },
  },
  title: {
    fontFamily: '"Anton", sans-serif',
    fontSize: '32px',
    fontWeight: 400,
    color: '#102E65',
    lineHeight: 1.2,
    textTransform: 'uppercase',
    '@media (min-width: 768px)': {
      fontSize: '48px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '60px',
    },
  },
  description: {
    fontSize: '14px',
    lineHeight: 1.6,
    color: '#4B5563',
    '@media (min-width: 768px)': {
      fontSize: '16px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '18px',
    },
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    marginTop: '16px',
    '@media (min-width: 768px)': {
      gap: '26px',
      marginTop: '20px',
    },
    '@media (min-width: 1024px)': {
      gap: '32px',
      marginTop: '24px',
    },
  },
  listItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    paddingBottom: '20px',
    borderBottom: '1px solid #E5E7EB',
    textAlign: 'left',
    '@media (min-width: 768px)': {
      gap: '20px',
      paddingBottom: '26px',
    },
    '@media (min-width: 1024px)': {
      gap: '24px',
      paddingBottom: '32px',
    },
  },
  lastItem: {
    borderBottom: 'none',
  },
  itemNumberContainer: {
    flexShrink: 0,
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#D3EDFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '@media (min-width: 768px)': {
      width: '36px',
      height: '36px',
    },
    '@media (min-width: 1024px)': {
      width: '40px',
      height: '40px',
    },
  },
  itemNumber: {
    color: '#102E65',
    fontSize: '14px',
    fontWeight: 'bold',
    '@media (min-width: 768px)': {
      fontSize: '16px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '18px',
    },
  },
  itemContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  itemTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#102E65',
    marginBottom: '6px',
    '@media (min-width: 768px)': {
      fontSize: '19px',
      marginBottom: '7px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '22px',
      marginBottom: '8px',
    },
  },
  itemDescription: {
    fontSize: '14px',
    color: '#4B5563',
    lineHeight: 1.5,
    '@media (min-width: 768px)': {
      fontSize: '15px',
    },
    '@media (min-width: 1024px)': {
      fontSize: '16px',
    },
  },
  imageContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    order: -1,
    '@media (min-width: 1024px)': {
      order: 0,
    },
  },
  image: {
    height: 'auto',
    maxHeight: '85vh',
    objectFit: 'contain',
    borderRadius: '16px',
    '@media (min-width: 768px)': {
      borderRadius: '20px',
      maxWidth: '90%',
    },
    '@media (min-width: 1024px)': {
      borderRadius: '24px',
      maxWidth: '100%',
    },
  },
})
